import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, FormControl, FormHelperText, FormLabel, Grid, MenuItem, Select, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import CommonButton from "../../../component/button/Button";
import addCircleIcon from "../../../assets/icons/add-circle.svg";
import { formatAmount, formatGermanAmount, formatPlainAmount, formatUpdateNumber } from "../../../helper/formatAmount";
import Download from '../../../assets/icons/download.svg';
import CommonModal from "../../../component/model/ModelComponent";
import TableComponent from "../../../component/Table/TableComponent";
import InputComponent from "../../../component/input/Input";
import Toast from "../../../component/toastify/Index";
import editIcon from "../../../assets/icons/Edit.svg";
import deleteIcon from "../../../assets/icons/trash.svg";
import { createKostenplanung, deleteKostenplanung, getByIdKostenplanung, getKostenplanungLists, updateKostenplanung, verbindlichkeitenOptions } from "../../../services/customer-services/customerKostenplanungService";
import GeneratePDF from "../../../component/PDFGenerator";

const columns = [
    { field: "kunde", headerName: "Kunde", translationKey: 'forderungen_kunde' },
    { field: "nettoBetrag", headerName: "Netto Betrag", translationKey: 'forderungen_nettoBetrag' },
    { field: "bruttoBetrag", headerName: "Brutto Betrag", translationKey: 'forderungen_bruttoBetrag' },
    { field: "ausVorwoche", headerName: "AUS Vorwoche", translationKey: 'umsatzplanung_aus_vorwoche' },
    { field: "bemerkung", headerName: "Bemerkung", translationKey: 'umsatzplanung_bemerkung' },
    { field: "action", headerName: "Aktion", translationKey: 'client_list_action' },
];

const Kostenplanung = ({ shouldDisableDate, selectedWeek, setTotals, clientId }) => {
    const { t } = useTranslation();
    const [kostenplanungs, setKostenplanungs] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        kunde: '',
        rechnungsNummer: null,
        datum: null,
        nettoBetrag: '',
        umsatzsteuer: '',
        steuer: '',
        bruttoBetrag: '',
        rechnungsart: '',
        bemerkung: '',
        ausVorwoche: '',
    });
    const [errors, setErrors] = useState({});
    const [debouncedNettoBetrag, setDebouncedNettoBetrag] = useState(formData?.nettoBetrag || '');
    const [timer, setTimer] = useState(null);
    const [options, setOptions] = useState([]);

    useEffect(() => {
        if (!debouncedNettoBetrag || !formData.umsatzsteuer) return;

        const netto = parseFloat(debouncedNettoBetrag?.split(",")[0]?.replace(".", ""));
        const steuerPercent = parseFloat(formData.umsatzsteuer);

        if (!isNaN(netto) && !isNaN(steuerPercent)) {
            const steuerAmount = (netto * steuerPercent) / 100;
            setFormData(prev => ({
                ...prev,
                steuer: steuerAmount,
                bruttoBetrag: (Number(netto) + Number(steuerAmount))
            }));
        }
    }, [debouncedNettoBetrag, formData.umsatzsteuer]);

    const getKostenplanung = async () => {
        if (clientId) {
            const payload = {
                pageNo,
                perPage,
                year: selectedWeek?.year,
                weekNumber: selectedWeek?.weekNumber,
                clientId
            }
            const response = await getKostenplanungLists(payload);
            if (response?.isSuccess) {
                setKostenplanungs([...response.data]);
                setTotalRecords(response.totalRecord);
                const totalNetto = response.data?.reduce((sum, invoice) => sum + Number(invoice?.nettoBetrag), 0);
                const totalBrutto = response.data?.reduce((sum, invoice) => sum + Number(invoice?.bruttoBetrag), 0);
                setTotals(prevTotals => ({
                    ...prevTotals,
                    kostenplanungNetto: totalNetto,
                    kostenplanungBrutto: totalBrutto,
                }));
            }
            else {
                setKostenplanungs([]);
                setTotalRecords(0);
                setTotals(prevTotals => ({
                    ...prevTotals,
                    kostenplanungNetto: 0,
                    kostenplanungBrutto: 0,
                }));
            }
        }
    }

    useEffect(() => {
        getKostenplanung()
    }, [pageNo, perPage, selectedWeek, clientId]);

    const getverbindlichkeitenOptions = async () => {
        if (clientId) {
            const response = await verbindlichkeitenOptions(clientId);
            if (response?.isSuccess) {
                setOptions([...response.data]);
            }
            else {
                setOptions([]);
            }
        }
    }

    useEffect(() => {
        getverbindlichkeitenOptions();
    }, []);


    // Handle date change
    const handleDateChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        setErrors(prev => ({
            ...prev,
            [name]: "",
        }));
    };

    useEffect(() => {
        const debounceTime = setTimeout(() => {
            setFormData((prev) => ({ ...prev, nettoBetrag: debouncedNettoBetrag }));
        }, 1000)
        return () => clearTimeout(debounceTime)
    }, [debouncedNettoBetrag])

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'nettoBetrag') {
            // Immediately set the raw value in the form state to avoid losing user input while typing
            setFormData((prev) => ({ ...prev, [name]: value }));

            // Clear the previous timer if it's still active
            if (timer) {
                clearTimeout(timer);
            }

            // Set a new timer for debouncing the formatted value update
            const newTimer = setTimeout(() => {
                // Format the value only after the debounce delay
                const formattedValue = formatGermanAmount(value);
                setDebouncedNettoBetrag(formattedValue);  // Update debounced value after the delay
            }, 500); // 500 ms debounce delay

            setTimer(newTimer);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.kunde) {
            newErrors.kunde = `${t('forderungen_kunde')} ${t('is_required')}`;
        }

        if (!formData.rechnungsNummer) {
            newErrors.rechnungsNummer = `${t('forderungen_rechnungsNummer')} ${t('is_required')}`;
        }

        if (!formData.datum) {
            newErrors.datum = `${t('umsatzplanung_datum')} ${t('is_required')}`;
        }

        if (!formData.nettoBetrag) {
            newErrors.nettoBetrag = `${t('forderungen_nettoBetrag')} ${t('is_required')}`;
        }
        else {
            const germanNumber = formData?.nettoBetrag?.replace(/\./g, '')?.replace(/,/g, '.');
            const nettoValue = Number(germanNumber);

            if (isNaN(nettoValue) || !Number.isFinite(nettoValue)) {
                newErrors.nettoBetrag = t('verbindlicheitem_netto_betrag_valid_number');
            } else if (nettoValue <= 0) {
                newErrors.nettoBetrag = `${t('forderungen_nettoBetrag')} ${t('cannot_be_negative')}`;
            }
        }

        if (!formData.umsatzsteuer) {
            newErrors.umsatzsteuer = `${t('forderungen_umsatzsteuer')} ${t('is_required')}`;
        }

        if (!formData.bemerkung) {
            newErrors.bemerkung = `${t('umsatzplanung_bemerkung')} ${t('is_required')}`;
        }

        if (!formData.ausVorwoche) {
            newErrors.ausVorwoche = `${t('umsatzplanung_aus_vorwoche')} ${t('is_required')}`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const toCleanNumberString = (value) => {
        if (value == null) return "0";
        const str = String(value);
        const num = Number(str?.replace(/\./g, '')?.replace(',', '.'));
        return String(num);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const datum = dayjs(formData?.datum).format("DD-MM-YYYY");
        const nettoBetrag = formatPlainAmount(formData?.nettoBetrag);
        const monthNumber = dayjs(formData?.datum).month() + 1;
        const year = dayjs(formData?.datum).year();
        const steuer = toCleanNumberString(formData?.steuer);
        const bruttoBetrag = toCleanNumberString(formData?.bruttoBetrag);

        const payload = {
            year: selectedWeek?.year,
            weekNumber: selectedWeek?.weekNumber,
            startDate: dayjs(selectedWeek?.weekStart).format("DD-MM-YYYY"),
            endDate: dayjs(selectedWeek?.weekEnd).format("DD-MM-YYYY"),
            kostenplanung: {
                ...formData,
                datum,
                steuer,
                bruttoBetrag,
                nettoBetrag,
                calendarMonth: monthNumber,
                kundeModel: "Verbindlichkeiten",
                calendarYear: year
            },
            clientId
        }

        let response = [];
        if (editId) {
            response = await updateKostenplanung({
                ...payload,
                id: editId
            });
        }
        else {
            response = await createKostenplanung({
                ...payload,
            });
        }
        if (response?.isSuccess) {
            Toast(response?.message, "success");
            setIsModalOpen(false);
            getKostenplanung();
            setFormData({
                kunde: '',
                rechnungsNummer: null,
                datum: null,
                nettoBetrag: '',
                umsatzsteuer: '',
                steuer: '',
                bruttoBetrag: '',
                rechnungsart: '',
                bemerkung: '',
                ausVorwoche: '',
            });
            setDebouncedNettoBetrag('');
            setTimer(null);
            setEditId(null);
        }
    }

    const handleEdit = async (editId) => {
        const response = await getByIdKostenplanung(editId, clientId);
        setFormData({
            ...response.data,
            datum: response.data?.datum ? dayjs(response.data?.datum) : null,
            nettoBetrag: formatUpdateNumber(Number(response.data?.nettoBetrag)),
            bruttoBetrag: formatUpdateNumber(Number(response.data?.bruttoBetrag)),
            steuer: formatUpdateNumber(Number(response.data?.steuer)),
        });
        setDebouncedNettoBetrag(formatUpdateNumber(Number(response.data?.nettoBetrag)))
        setEditId(editId);
        setIsModalOpen(true);
        setErrors({});
    };

    const handleDelete = async (deleteId) => {
        const response = await deleteKostenplanung(deleteId, clientId);
        if (response?.isSuccess) {
            Toast(response?.message, "success");
            getKostenplanung();
        }
    };

    const handleAdd = () => {
        setIsModalOpen(true);
    };

    const totalNetto = kostenplanungs?.reduce((sum, invoice) => sum + Number(invoice?.nettoBetrag), 0);
    const totalBrutto = kostenplanungs?.reduce((sum, invoice) => sum + Number(invoice?.bruttoBetrag), 0);

    // Generate PDF 
    const excludedKeys = ['_id', 'createdAt', 'updatedAt', 'isDeleted', '__v', 'rechnungsart', 'company', "datum", 'customers', 'startDate', 'steuer', 'kostenplanungId',
        'umsatzsteuer', 'weekNumber', 'year', 'endDate', 'rechnungsNummer'];

    const customLabels = {
        kunde: 'Kunde',
        nettoBetrag: 'Netto Betrag',
        bruttoBetrag: 'Brutto Betrag',
        ausVorwoche: 'AUS Vorwoche',
        bemerkung: 'Bemerkung',
    };

    const customColumnOrder = [
        'kunde',
        'nettoBetrag',
        'bruttoBetrag',
        'ausVorwoche',
        'bemerkung',
    ];

    const formatCell = (key, value) => {
        if (['bruttoBetrag', 'nettoBetrag']?.includes(key)) {
            return formatAmount(value) + " €";
        }

        return value || '-';
    };

    return (
        <Box
            sx={{
                paddingTop: 1
            }}
        >
            <CommonModal
                open={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false); setFormData({
                        kunde: '',
                        rechnungsNummer: null,
                        datum: null,
                        nettoBetrag: '',
                        umsatzsteuer: '',
                        steuer: '',
                        bruttoBetrag: '',
                        rechnungsart: '',
                        bemerkung: '',
                        ausVorwoche: '',
                    });
                    setDebouncedNettoBetrag('');
                    setTimer(null);
                    setEditId(null);
                }}
                title={t('umsatzplanung_kostenplanung')}
                className="model-title"
                width={600}
            >
                <Typography
                    sx={{
                        border: "1px solid #E3E3E3",
                        padding: "20px",
                        borderRadius: "10px",
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} lg={6}>
                                <FormControl fullWidth error={!!errors?.kunde}>
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('forderungen_kunde')}
                                    </FormLabel>
                                    <Autocomplete
                                        options={options || []}
                                        getOptionLabel={(option) => option.label}
                                        onChange={(event, newValue) => {
                                            handleChange(event);
                                            setFormData({
                                                ...formData,
                                                kunde: newValue?.value || "",  // Store the value property
                                            });
                                        }}
                                        popupIcon={<ExpandMoreIcon sx={{ fontSize: "30px", color: "#33333399" }} />}
                                        value={options?.find(opt => opt?.value === formData?.kunde) || null}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "10px",
                                                border: "1px solid rgba(227, 227, 227, 0.57)"
                                            },
                                        }}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                name="kunde"
                                                placeholder={t('stammdaten_placeholder_branche')}
                                                sx={{
                                                    "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                                                        padding: 0
                                                    }
                                                }} />}
                                    />
                                    {errors?.kunde && (
                                        <FormHelperText
                                            sx={{
                                                color: "#d32f2f",
                                                margin: "5px 0 0 0 ",
                                                fontSize: "14px",
                                                fontFamily: "Poppins"
                                            }}
                                        >
                                            {errors?.kunde}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'0'}
                                    label={t('forderungen_rechnungsNummer')}
                                    className="input-field"
                                    name={'rechnungsNummer'}
                                    value={formData?.rechnungsNummer || ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors.rechnungsNummer}
                                    helperText={errors.rechnungsNummer}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    adapterLocale="de"
                                >
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('umsatzplanung_datum')}
                                    </FormLabel>
                                    <DatePicker
                                        value={formData.datum}
                                        onChange={(date) => handleDateChange("datum", date)}
                                        shouldDisableDate={shouldDisableDate}
                                        referenceDate={selectedWeek?.weekStart}
                                        format="DD.MM.YYYY"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#826320',
                                                },
                                            },
                                        }}
                                        slotProps={{
                                            textField: {
                                                error: !!errors.datum,
                                                helperText: errors.datum || '',
                                                fullWidth: true,
                                                InputProps: {
                                                    sx: {
                                                        borderRadius: '10px',
                                                        border: '2px solid #E3E3E3',
                                                        '& .MuiInputBase-input': {
                                                            padding: '10px 12px',
                                                        },
                                                        '& input::placeholder': {
                                                            color: '#000',
                                                            fontSize: '14px',
                                                            opacity: 1,
                                                            fontFamily: 'Poppins'
                                                        },
                                                    }
                                                },
                                                FormHelperTextProps: {
                                                    sx: {
                                                        color: !!errors.datum ? '#d32f2f' : '#14A9E2',
                                                        margin: '5px 0 0 0',
                                                        fontSize: '14px',
                                                        fontFamily: 'Poppins'
                                                    }
                                                }
                                            }
                                        }}
                                    />

                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'100.000,00 EUR'}
                                    label={t('forderungen_nettoBetrag')}
                                    className="input-field"
                                    name={'nettoBetrag'}
                                    value={formData?.nettoBetrag ?? ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors.nettoBetrag}
                                    helperText={errors.nettoBetrag}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <FormControl
                                    fullWidth
                                    error={!!errors?.umsatzsteuer}
                                >
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('forderungen_umsatzsteuer')}
                                    </FormLabel>
                                    <Select
                                        className="select-input"
                                        fullWidth
                                        value={formData?.umsatzsteuer || ""}
                                        onChange={(event) => {
                                            handleChange(event);
                                            setFormData({
                                                ...formData,
                                                umsatzsteuer: event.target.value,
                                            });
                                        }}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#826320 !important',
                                                },
                                            },
                                        }}
                                        inputProps={{
                                            name: 'umsatzsteuer',
                                            IconComponent: () => (
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        paddingRight: "10px",
                                                    }}
                                                >
                                                    <ExpandMoreIcon
                                                        sx={{ fontSize: "30px", color: "#33333399" }}
                                                    />
                                                </span>
                                            ),
                                        }}
                                        style={{
                                            padding: "0",
                                            borderRadius: "10px",
                                            border: "1px solid #e3e3e391",
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#999' }}>7%</span>;
                                            }
                                            return selected;
                                        }}
                                    >
                                        {/* Options */}
                                        {[{ value: "0", label: "0%" }, { value: "7", label: "7%" }, { value: "19", label: "19%" }].map((option, i) => (
                                            <MenuItem key={i} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors?.umsatzsteuer && (
                                        <FormHelperText sx={{ margin: "0", marginTop: "8px", color: "#d32f2f", fontSize: "14px", fontFamily: "Poppins" }}>{errors?.umsatzsteuer}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'100.000,00 EUR'}
                                    label={t('forderungen_steuer')}
                                    disabled
                                    className="input-field"
                                    name={'steuer'}
                                    value={formData?.steuer ? formatAmount(formData?.steuer) : ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors?.steuer}
                                    helperText={errors?.steuer}
                                />
                            </Grid>
                            {/* BruttoBetrag */}
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'100.000,00 EUR'}
                                    label={t('forderungen_bruttoBetrag')}
                                    className="input-field"
                                    name={'bruttoBetrag'}
                                    value={formData?.bruttoBetrag ? formatAmount(formData?.bruttoBetrag) : ""}
                                    onChange={handleChange}
                                    disabled
                                    required
                                    error={!!errors?.bruttoBetrag}
                                    helperText={errors?.bruttoBetrag}
                                />
                            </Grid>

                            {/* AUSVorwoche */}
                            <Grid item xs={12} lg={6}>
                                <FormControl
                                    fullWidth
                                    error={!!errors?.ausVorwoche}
                                >
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('umsatzplanung_aus_vorwoche')}
                                    </FormLabel>
                                    <Select
                                        className="select-input"
                                        fullWidth
                                        value={formData?.ausVorwoche ? t(`common_${formData?.ausVorwoche?.toLowerCase()}`) : ""}
                                        onChange={(event) => {
                                            handleChange(event);
                                            setFormData({
                                                ...formData,
                                                ausVorwoche: event.target.value,
                                            });
                                        }}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#826320 !important',
                                                },
                                            },
                                        }}
                                        inputProps={{
                                            name: 'ausVorwoche',
                                            IconComponent: () => (
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        paddingRight: "10px",
                                                    }}
                                                >
                                                    <ExpandMoreIcon
                                                        sx={{ fontSize: "30px", color: "#33333399" }}
                                                    />
                                                </span>
                                            ),
                                        }}
                                        style={{
                                            padding: "0",
                                            borderRadius: "10px",
                                            border: "1px solid #e3e3e391",
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#999' }}>{t('common_ja')}</span>;
                                            }
                                            return selected;
                                        }}
                                    >
                                        {/* Options */}
                                        {[{ value: "Ja", label: t('common_ja') }, { value: "Nein", label: t('common_nein') }].map((option, i) => (
                                            <MenuItem key={i} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors?.ausVorwoche && (
                                        <FormHelperText sx={{ margin: "0", marginTop: "8px", color: "#d32f2f", fontSize: "14px", fontFamily: "Poppins" }}>{errors?.ausVorwoche}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            {/* Bemerkung */}
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={t('umsatzplanung_bemerkung')}
                                    label={t('umsatzplanung_bemerkung')}
                                    className="input-field"
                                    name={'bemerkung'}
                                    value={formData?.bemerkung ?? ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors.bemerkung}
                                    helperText={errors.bemerkung}
                                />
                            </Grid>
                        </Grid>
                        <Typography className="model-action-btn">

                            <CommonButton
                                text={t("forderungen_zurücksetzen")}
                                className="invitation-btn button-border-radius"
                                bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);"
                                onClick={() => {
                                    setFormData({
                                        kunde: '',
                                        rechnungsNummer: null,
                                        datum: null,
                                        nettoBetrag: '',
                                        umsatzsteuer: '',
                                        steuer: '',
                                        bruttoBetrag: '',
                                        rechnungsart: '',
                                        bemerkung: '',
                                        ausVorwoche: '',
                                    });
                                    setDebouncedNettoBetrag('');
                                    setTimer(null);
                                    setErrors({})
                                }}
                            />
                            <CommonButton
                                type="submit"
                                text={`${t("forderungen_speichern")}`}
                                className="add-user-btn button-border-radius"
                            />
                        </Typography>
                    </form>
                </Typography>
            </CommonModal>
            <Box
                sx={{
                    px: 2,
                    pt: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', },
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {t('umsatzplanung_kostenplanung')}
                </Typography>
                <CommonButton
                    text={`${t('stammdaten_add')} ${t('umsatzplanung_kostenplanung')}`}
                    onClick={handleAdd}
                    icon={
                        <img src={addCircleIcon} alt="addCircleIcon" className="button-icon-padding" />
                    }
                    className="add-user-btn button-border-radius"
                />
            </Box>
            <Box
                sx={{
                    paddingLeft: "15px",
                    paddingRight: "15px",
                }}
            >
                <TableComponent
                    data={kostenplanungs}
                    setPageNo={setPageNo}
                    pageNo={pageNo}
                    perPage={perPage}
                    setPerPage={setPerPage}
                    totalRecords={totalRecords}
                    columns={columns}
                    className={'table-max-height'}
                >
                    {kostenplanungs?.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                            <TableCell>{row.kunde}</TableCell>
                            <TableCell>{formatAmount(row.nettoBetrag) + " €"}</TableCell>
                            <TableCell>{formatAmount(row.bruttoBetrag) + " €"}</TableCell>
                            <TableCell>{row?.ausVorwoche ? t(`common_${row?.ausVorwoche?.toLowerCase()}`) : "-"}</TableCell>
                            <TableCell>{row?.bemerkung}</TableCell>
                            <TableCell>
                                <Typography
                                    className="action"
                                    classes="flex"
                                    alignItems={"center"}
                                    display={"flex"}
                                >
                                    <span onClick={() => handleEdit(row.kostenplanungId)}>
                                        <img src={editIcon} alt="edit icon" className="edit-icon" />
                                    </span>
                                    <span onClick={() => handleDelete(row.kostenplanungId)}>
                                        <img src={deleteIcon} alt="delete icon" />
                                    </span>
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableComponent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: "1rem", marginTop: "1.75rem" }}>
                    <Typography>
                        {t('common_sum')} ({t('forderungen_netto')}):  {formatAmount(totalNetto) + " €"}
                    </Typography>
                    <Typography>
                        {t('common_sum')} ({t('forderungen_brutto')}): {formatAmount(totalBrutto) + " €"}
                    </Typography>
                    <GeneratePDF
                        data={kostenplanungs}
                        excludedKeys={excludedKeys}
                        customLabels={customLabels}
                        customColumnOrder={customColumnOrder}
                        customRowsPerPage={30}
                        formatCell={formatCell}
                    />
                </Box>
            </Box>
        </Box>
    )
}

export default Kostenplanung;
